/**
 * PrivacyCard — DSGVO-Anzeige im Terminal + Feiertage + Betriebsfreie Tage.
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Separator } from '../../components/ui/separator';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Shield } from 'lucide-react';

const TERMINAL_MODES = [
  { value: '1', title: 'Vollständiger Name + Personal-Nr.', example: 'Beispiel: Max Mustermann (MA-2026-001)' },
  { value: '2', title: 'Initialen + Personal-Nr.', example: 'Beispiel: M.M. (MA-2026-001)' },
  { value: '3', title: 'Nur Initialen', example: 'Beispiel: M.M.' },
  { value: '4', title: 'Nur Personal-Nr.', example: 'Beispiel: MA-2026-001' },
  { value: '5', title: 'Erster Buchstabe Vorname + Nachname', example: 'Beispiel: M. Mustermann' },
];

export const PrivacyCard = ({ formData, onChange }) => (
  <Card className="border-border/60">
    <CardHeader>
      <CardTitle className="font-heading flex items-center gap-2">
        <Shield className="h-5 w-5 text-primary" />
        Datenschutz (DSGVO)
      </CardTitle>
      <CardDescription>Einstellungen für die Anzeige im Zeiterfassungs-Terminal &amp; in der Einsatzplan-TV-Ansicht</CardDescription>
    </CardHeader>
    <CardContent className="space-y-4">
      <div className="space-y-3">
        <Label>Mitarbeiteranzeige (Terminal &amp; TV)</Label>
        <p className="text-xs text-muted-foreground">
          Bei Namensgleichheit wird der Vornamen-Präfix automatisch erweitert (z.B. „Ma. Schäler" vs. „Mo. Schäler").
          Hinweis: „Nur Personal-Nr." gilt nur fürs Terminal — die TV-Ansicht zeigt dann „V. Nachname", damit der Schichtplan lesbar bleibt.
        </p>
        <RadioGroup
          value={String(formData.terminal_display_mode)}
          onValueChange={(value) => onChange('terminal_display_mode', parseInt(value))}
          className="space-y-3"
        >
          {TERMINAL_MODES.map((mode) => (
            <div key={mode.value} className="flex items-start space-x-3 p-3 rounded-lg border bg-muted/30 hover:bg-muted/50 transition-colors">
              <RadioGroupItem
                value={mode.value}
                id={`mode-${mode.value}`}
                className="mt-0.5"
                data-testid={mode.value === '5' ? 'terminal-mode-5' : undefined}
              />
              <div className="space-y-1">
                <Label htmlFor={`mode-${mode.value}`} className="font-medium cursor-pointer">
                  {mode.title}
                </Label>
                <p className="text-xs text-muted-foreground">{mode.example}</p>
              </div>
            </div>
          ))}
        </RadioGroup>
      </div>

      <Separator className="my-4" />

      <div className="space-y-3">
        <Label>Feiertage in der Zeiterfassung</Label>
        <div className="flex items-start space-x-3 p-3 rounded-lg border bg-muted/30">
          <input
            type="checkbox"
            id="holiday-auto-credit"
            checked={formData.holiday_auto_credit}
            onChange={(e) => onChange('holiday_auto_credit', e.target.checked)}
            className="h-4 w-4 mt-0.5 rounded border-gray-300"
            data-testid="holiday-auto-credit-checkbox"
          />
          <div className="space-y-1">
            <Label htmlFor="holiday-auto-credit" className="font-medium cursor-pointer">
              Automatische Stunden-Gutschrift an Feiertagen
            </Label>
            <p className="text-xs text-muted-foreground">
              An Feiertagen werden automatisch die Tagesstunden gutgeschrieben (z.B. 8h bei 40h-Woche).
              Mitarbeiter müssen an Feiertagen nicht stempeln.
            </p>
          </div>
        </div>
      </div>

      <Separator className="my-4" />

      <div className="space-y-3">
        <Label>Abwesenheiten &amp; Soll-Stunden</Label>
        <div className="flex items-start space-x-3 p-3 rounded-lg border bg-muted/30">
          <input
            type="checkbox"
            id="ko-tag-enabled"
            checked={formData.ko_tag_enabled !== false}
            onChange={(e) => onChange('ko_tag_enabled', e.target.checked)}
            className="h-4 w-4 mt-0.5 rounded border-gray-300"
            data-testid="ko-tag-enabled-checkbox"
          />
          <div className="space-y-1">
            <Label htmlFor="ko-tag-enabled" className="font-medium cursor-pointer">
              KO-Tag (Krank ohne Krankenschein) erlauben
            </Label>
            <p className="text-xs text-muted-foreground">
              Wenn deaktiviert, kann der Abwesenheitstyp „KO-Tag" nicht mehr ausgewählt
              werden. Bestehende Einträge bleiben erhalten.
            </p>
          </div>
        </div>
        <div className="flex items-start space-x-3 p-3 rounded-lg border bg-muted/30">
          <input
            type="checkbox"
            id="deduct-absences"
            checked={formData.deduct_absences_from_target !== false}
            onChange={(e) => onChange('deduct_absences_from_target', e.target.checked)}
            className="h-4 w-4 mt-0.5 rounded border-gray-300"
            data-testid="deduct-absences-checkbox"
          />
          <div className="space-y-1">
            <Label htmlFor="deduct-absences" className="font-medium cursor-pointer">
              Abwesenheiten von Soll-Stunden abziehen
            </Label>
            <p className="text-xs text-muted-foreground">
              Tage mit K, KK, EZ oder KO werden — wie Feiertage — von den Soll-Stunden
              abgezogen, sodass keine Minusstunden entstehen.
            </p>
          </div>
        </div>
      </div>

      <Separator className="my-4" />

      <div className="space-y-3">
        <Label>Personalanalyse – Vollzeit-Schwelle</Label>
        <div className="flex items-center gap-3 p-3 rounded-lg border bg-muted/30">
          <div className="flex items-center gap-2">
            <Input
              type="number"
              min={1}
              max={60}
              step={0.5}
              value={formData.fulltime_threshold_hours ?? 39}
              onChange={(e) => onChange('fulltime_threshold_hours', e.target.value === '' ? '' : parseFloat(e.target.value))}
              className="w-24 h-9"
              data-testid="fulltime-threshold-input"
            />
            <span className="text-sm text-muted-foreground">Wochenstunden</span>
          </div>
          <p className="text-xs text-muted-foreground flex-1">
            Ab dieser wöchentlichen Arbeitszeit gilt ein Mitarbeiter in der Personalanalyse als
            <span className="font-medium"> Vollzeit</span>, darunter als Teilzeit (Standard: 39h).
          </p>
        </div>
      </div>

      <Separator className="my-4" />

      <div className="space-y-3">
        <Label>Betriebsfreie Tage</Label>
        <p className="text-xs text-muted-foreground">
          Diese Tage gelten als betriebsfrei. Es wird kein Urlaubstag abgezogen.
        </p>
        <FreeDayCheckbox
          id="free-christmas-eve"
          checked={formData.free_christmas_eve}
          onChange={(v) => onChange('free_christmas_eve', v)}
          title="24. Dezember (Heiligabend) betriebsfrei"
          description="Heiligabend ist automatisch frei, ohne Urlaubsabzug."
        />
        <FreeDayCheckbox
          id="free-new-years-eve"
          checked={formData.free_new_years_eve}
          onChange={(v) => onChange('free_new_years_eve', v)}
          title="31. Dezember (Silvester) betriebsfrei"
          description="Silvester ist automatisch frei, ohne Urlaubsabzug."
        />
      </div>
    </CardContent>
  </Card>
);

const FreeDayCheckbox = ({ id, checked, onChange, title, description }) => (
  <div className="flex items-start space-x-3 p-3 rounded-lg border bg-muted/30">
    <input
      type="checkbox"
      id={id}
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className="h-4 w-4 mt-0.5 rounded border-gray-300"
      data-testid={`${id}-checkbox`}
    />
    <div className="space-y-1">
      <Label htmlFor={id} className="font-medium cursor-pointer">{title}</Label>
      <p className="text-xs text-muted-foreground">{description}</p>
    </div>
  </div>
);
